import type { HistoryRange } from "../../shared/api.js";
import { compareVersions } from "../../shared/butlerVersion.js";
import { DEFAULT_GUARD, kindLabel, POWER_BUTLER, POWER_MODES, TANK_BUTLER, type MachineView, type PowerMode, type PowerStatus } from "../../shared/power.js";
import { stackKey } from "../../shared/stack.js";
import { Page } from "../Page.js";
import { h, patch } from "../dom.js";
import { historyChart } from "../components/HistoryChart.js";
import { ago, duration } from "../format.js";

const PLAN = "power/power-control-plan.md in the project files, or the Butler README's Power control section";

const MODE_TEXT: Record<PowerMode, string> = {
  manual: "Manual",
  auto: "Auto (follows the LSC)",
  on: "Always on",
  off: "Always off",
};

const MODE_HELP = "Manual: only its guard switches it off. Auto: on when the LSC is low, off when it is full. Always on / off: held there (the guard still wins).";

/** Guard stacks offered in the editor. */
const GUARDS: Array<{ key: string; label: string; stack: { type: "fluid"; name: string } }> = [
  { key: "fluid:ic2distilledwater", label: "Distilled water", stack: { type: "fluid", name: "ic2distilledwater" } },
  { key: "fluid:ic2coolant", label: "IC2 coolant", stack: { type: "fluid", name: "ic2coolant" } },
  { key: "fluid:water", label: "Water", stack: { type: "fluid", name: "water" } },
];

const KIND_ORDER: Record<string, number> = { lsc: 0, deph: 1, turbine: 2, tank: 3, machine: 4 };

/** 1.2M EU, 3.4T EU... with SI prefixes past tera. */
export function eu(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "?";
  const units = ["", "k", "M", "G", "T", "P", "E", "Z", "Y"];
  let v = Math.abs(n);
  let i = 0;
  while (v >= 1000 && i < units.length - 1) {
    v /= 1000;
    i++;
  }
  const text = i === 0 ? String(Math.round(v)) : v.toFixed(v >= 100 ? 0 : v >= 10 ? 1 : 2);
  return `${n < 0 ? "−" : ""}${text}${units[i]}`;
}

function pct(f: number): string {
  return `${(f * 100).toFixed(f < 0.1 ? 1 : 0)}%`;
}

function machineTitle(m: MachineView): string {
  return m.label ?? `${kindLabel(m.kind)} ${m.address.slice(0, 4)}`;
}

/** LSC charge, the power rules, and every GregTech machine the Butler reaches. */
export class PowerPage extends Page {
  readonly title = "Power";
  private status: PowerStatus | null = null;
  private range: HistoryRange = "24h";
  /** Machine whose name is being edited in place. */
  private renaming: string | null = null;
  private showHidden = false;
  private readonly banner = h("div");
  private readonly lscCard = h("div", { class: "card power-card" });
  private readonly controlCard = h("form", { class: "card power-card power-control" });
  private readonly chartCard = h("div", { class: "card" });
  private readonly machinesBar = h("div", { class: "toolbar machines-bar" });
  private readonly grid = h("div", { class: "machine-grid" });

  async mount(): Promise<void> {
    this.controlCard.addEventListener("submit", (e) => {
      e.preventDefault();
      void this.saveControl();
    });
    this.el.append(this.banner, h("div", { class: "power-top" }, this.lscCard, this.controlCard), this.chartCard, this.machinesBar, this.grid);
    this.listen("machines", () => {
      if (this.ctx.live.machines) this.show(this.ctx.live.machines);
    });
    this.listen("link", () => void this.reload());
    this.every(60_000, () => void this.loadChart());
    await this.reload();
    await this.loadChart();
  }

  private async reload(): Promise<void> {
    const status = await this.attempt(() => this.ctx.api.power());
    if (status) this.show(status);
  }

  private show(status: PowerStatus): void {
    this.status = status;
    this.renderBanner();
    this.renderLsc();
    this.renderControl();
    this.renderMachines();
  }

  // ------------------------------------------------------------- banner

  private renderBanner(): void {
    const s = this.status!;
    const notes: HTMLElement[] = [];
    if (!s.connected) {
      notes.push(h("p", { class: "warn" }, "The game is not connected. The Butler keeps applying the power rules it saved while the app is away."));
    } else if (!s.supported) {
      notes.push(h("p", { class: "warn" }, `The Butler (v${s.butlerVersion ?? "?"}) is too old for power control: update it to ${POWER_BUTLER} or later with butler-update.`));
    } else if (s.at === null || s.machines.length === 0) {
      notes.push(
        h("p", null, "No GregTech machine reported yet."),
        h(
          "ul",
          { class: "small" },
          h("li", null, "Place an Adapter against each controller (the block with the GUI) and cable it to the Butler computer, or"),
          h("li", null, "bind an MFU to the controller (shift-right-click) and put it in an Adapter on the Butler's network, up to 16 blocks away, or"),
          h("li", null, "for a far power plant, run butler-node on a computer there with a linked card (any distance) or a tier 2 wireless card (400 blocks)."),
        ),
        h("p", { class: "muted small" }, `Run "butler power" on the computer to see what it finds. Details: ${PLAN}.`),
      );
    }
    this.banner.className = notes.length ? "card notice" : "";
    patch(this.banner, ...notes);
  }

  // ------------------------------------------------------------- LSC

  private renderLsc(): void {
    const s = this.status!;
    const lsc = s.lsc;
    const m = lsc ? s.machines.find((x) => x.id === lsc.id) : undefined;
    if (!lsc || !m) {
      patch(this.lscCard, h("h2", null, "Lapotronic Supercapacitor"), h("p", { class: "muted" }, "No LSC reported. The automatic rules need one to know when to charge."));
      return;
    }
    const fill = lsc.capacity > 0 ? lsc.stored / lsc.capacity : 0;
    const low = s.config.low;
    const high = s.config.high;
    const f = m.facts;
    const net = lsc.netPerTick;
    const rows: Array<[string, string]> = [];
    if (net !== null) rows.push(["Net", `${net >= 0 ? "+" : ""}${eu(net)} EU/t`]);
    if (lsc.secondsToFull !== null) rows.push(["Full in", duration(lsc.secondsToFull * 1000)]);
    else if (lsc.secondsToEmpty !== null) rows.push(["Empty in", duration(lsc.secondsToEmpty * 1000)]);
    else if (f.timeToFull) rows.push(["Full in", f.timeToFull]);
    else if (f.timeToEmpty) rows.push(["Empty in", f.timeToEmpty]);
    if (f.avgIn !== undefined) rows.push(["In (5 s)", `${eu(f.avgIn)} EU/t`]);
    if (f.avgOut !== undefined) rows.push(["Out (5 s)", `${eu(f.avgOut)} EU/t`]);
    if (f.avgIn1h !== undefined) rows.push(["In (1 h)", `${eu(f.avgIn1h)} EU/t`]);
    if (f.avgOut1h !== undefined) rows.push(["Out (1 h)", `${eu(f.avgOut1h)} EU/t`]);
    if (f.passiveLoss !== undefined) rows.push(["Passive loss", `${eu(f.passiveLoss)} EU/t`]);
    if (f.maintenanceOk !== undefined) rows.push(["Maintenance", f.maintenanceOk ? "OK" : "Has problems"]);
    if (f.wireless !== undefined) rows.push(["Wireless", f.wireless ? "On" : "Off"]);
    patch(
      this.lscCard,
      h("div", { class: "card-head" }, h("h2", null, m.label ?? "Lapotronic Supercapacitor"), h("span", { class: "muted small" }, `Read ${ago(s.at)}${m.stale ? " (stale)" : ""}`)),
      h(
        "div",
        { class: "bar big" },
        h("div", { class: `fill ${fill <= low ? "low" : ""}`, style: `width:${Math.min(100, fill * 100)}%` }),
        h("div", { class: "min-mark", style: `left:${low * 100}%`, title: `Low ${pct(low)}` }),
        h("div", { class: "min-mark", style: `left:${high * 100}%`, title: `High ${pct(high)}` }),
      ),
      h("p", { class: "lsc-charge" }, h("strong", null, pct(fill)), ` · ${eu(lsc.stored)} / ${eu(lsc.capacity)} EU`),
      h("dl", { class: "stats two-col" }, ...rows.flatMap(([k, v]) => [h("dt", null, k), h("dd", null, v)])),
    );
  }

  // ------------------------------------------------------------- control

  private renderControl(): void {
    const s = this.status!;
    const c = s.config;
    const lscs = s.machines.filter((m) => m.kind === "lsc" && !m.error);
    const state = s.control?.state;
    const stateText =
      !c.enabled ? h("span", { class: "badge" }, "Off: reading only")
      : state === "charging" ? h("span", { class: "badge crafting" }, "Charging: auto machines on")
      : state === "full" ? h("span", { class: "badge ok" }, "Full: auto machines off")
      : h("span", { class: "badge backoff" }, "Holding");
    // Inputs keep what the user typed across live updates until saved (see patch()).
    patch(
      this.controlCard,
      h("div", { class: "card-head" }, h("h2", null, "Automatic control"), stateText),
      h(
        "div",
        { class: "power-form" },
        h(
          "label",
          { class: "check", title: "Off: the Butler only reads and reports. The rules run on the computer, so they keep working while the app is down." },
          h("input", { type: "checkbox", name: "enabled", checked: c.enabled }),
          "Butler switches machines",
        ),
        h("label", { class: "inline" }, "Charge at ≤", h("input", { type: "number", name: "low", class: "pct", min: "0", max: "99", step: "1", value: String(Math.round(c.low * 100)) }), "%"),
        h("label", { class: "inline" }, "Stop at ≥", h("input", { type: "number", name: "high", class: "pct", min: "1", max: "100", step: "1", value: String(Math.round(c.high * 100)) }), "%"),
        lscs.length > 1
          ? h(
              "label",
              { class: "inline" },
              "LSC",
              h(
                "select",
                { name: "lsc", title: "LSC the rules follow" },
                h("option", { value: "" }, "The first one found"),
                ...lscs.map((m) => h("option", { value: m.id, selected: m.id === c.lsc }, machineTitle(m))),
              ),
            )
          : null,
      ),
      h("div", { class: "form-foot" }, h("span", { class: "muted small" }, "Auto machines go on at the first mark and off at the second."), h("button", { class: "primary", type: "submit" }, "Save")),
    );
  }

  private async saveControl(): Promise<void> {
    const form = this.controlCard.elements;
    const value = (name: string) => (form.namedItem(name) as HTMLInputElement | HTMLSelectElement | null)?.value;
    const lsc = value("lsc");
    await this.attempt(async () => {
      this.show(
        await this.ctx.api.updatePowerConfig({
          enabled: (form.namedItem("enabled") as HTMLInputElement).checked,
          low: Number(value("low")) / 100,
          high: Number(value("high")) / 100,
          ...(lsc !== undefined ? { lsc: lsc || null } : {}),
        }),
      );
    }, "Power rules saved");
  }

  // ------------------------------------------------------------- chart

  private async loadChart(): Promise<void> {
    const history = await this.attempt(() => this.ctx.api.powerHistory(this.range));
    if (!history) return;
    const ranges: HistoryRange[] = ["1h", "6h", "24h", "7d", "30d"];
    const cfg = this.status?.config;
    patch(
      this.chartCard,
      h(
        "div",
        { class: "toolbar" },
        h("h2", null, "LSC charge"),
        h("span", { class: "spacer" }),
        ...ranges.map((r) =>
          h("button", { class: `small${r === this.range ? " primary" : ""}`, onclick: () => { this.range = r; void this.loadChart(); } }, r),
        ),
      ),
      historyChart({
        stack: { type: "item", name: "lsc", label: "LSC charge" },
        points: history.points.map(([at, stored, capacity]) => [at, capacity > 0 ? (stored / capacity) * 100 : 0]),
        from: history.from,
        to: history.to,
        min: (cfg?.low ?? 0) * 100,
        target: (cfg?.high ?? 0) * 100,
        refLabels: ["charge at", "stop at"],
        format: (v) => `${v.toFixed(v < 10 ? 1 : 0)}%`,
        yMax: 100,
        height: 140,
        emptyText: "No charge history yet. The app records the LSC once a minute while the game is connected.",
      }),
    );
  }

  // ------------------------------------------------------------- machines

  private renderMachines(): void {
    const s = this.status!;
    const all = [...s.machines].sort((a, b) => (KIND_ORDER[a.kind] ?? 9) - (KIND_ORDER[b.kind] ?? 9) || machineTitle(a).localeCompare(machineTitle(b)));
    const hidden = all.filter((m) => m.settings.hidden).length;
    if (!hidden) this.showHidden = false;
    const list = this.showHidden ? all : all.filter((m) => !m.settings.hidden);
    patch(
      this.machinesBar,
      h("h2", null, "Machines"),
      h("span", { class: "spacer" }),
      hidden
        ? h(
            "label",
            { class: "check small" },
            h("input", { type: "checkbox", checked: this.showHidden, onchange: (e: Event) => { this.showHidden = (e.currentTarget as HTMLInputElement).checked; this.renderMachines(); } }),
            `Show hidden (${hidden})`,
          )
        : null,
    );
    patch(this.grid, ...(list.length ? list.map((m) => this.machineCard(m)) : [h("p", { class: "muted" }, hidden ? "Every machine is hidden." : "None yet.")]));
  }

  private machineCard(m: MachineView): HTMLElement {
    const f = m.facts;
    const state =
      m.stale ? h("span", { class: "badge low" }, "No answer")
      : m.kind === "tank" ? null
      : m.allowed === false ? h("span", { class: "badge backoff" }, "Disabled")
      : m.active ? h("span", { class: "badge ok" }, "Running")
      : h("span", { class: "badge" }, "Idle");

    const facts: Array<[string, string]> = [];
    if (f.output !== undefined) facts.push(["Output", `${eu(f.output)} EU/t`]);
    if (f.efficiency !== undefined) facts.push([m.kind === "turbine" ? "Speed" : "Efficiency", `${f.efficiency}%`]);
    if (f.damage !== undefined) facts.push(["Rotor dmg", `${f.damage}%`]);
    if (f.flow !== undefined) facts.push(["Opt. flow", `${eu(f.flow)} L/${f.flowUnit ?? "t"}`]);
    if (f.fuel !== undefined) facts.push(["Fuel", `${eu(f.fuel)} L`]);
    if (f.problems !== undefined) facts.push(["Problems", String(f.problems)]);
    if (f.maintenanceOk !== undefined && m.kind !== "lsc") facts.push(["Maintenance", f.maintenanceOk ? "OK" : "Needed"]);
    if (m.kind !== "lsc" && m.maxProgress && m.maxProgress > 0 && m.active) facts.push(["Progress", `${Math.round(((m.progress ?? 0) / m.maxProgress) * 100)}%`]);
    if (m.kind === "lsc" && m.stored !== undefined && m.capacity) facts.push(["Charge", `${pct(m.stored / m.capacity)}`]);
    for (const t of m.tanks ?? []) {
      facts.push([t.label ?? t.name ?? "Empty", `${eu(t.amount)} L${t.capacity ? ` of ${eu(t.capacity)} (${pct(t.amount / t.capacity)})` : ""}`]);
    }
    if (m.kind === "tank" && m.tanks && !m.tanks.length) facts.push(["Contents", "Empty"]);
    const switchable = m.kind !== "lsc" && m.kind !== "tank";
    // A single fact (a tank's contents) reads better across the whole card.
    const wide = facts.length === 1 || facts.some(([, v]) => v.length > 12);

    const notes: HTMLElement[] = [];
    if (m.error) notes.push(h("p", { class: "small error" }, m.error));
    if (m.reason && m.desired !== undefined && m.desired !== null) {
      notes.push(h("p", { class: "small" }, `Wants ${m.desired ? "on" : "off"}: ${m.reason}`));
    }
    if (m.lastSwitch) {
      notes.push(h("p", { class: "muted small" }, `Switched ${m.lastSwitch.allowed ? "on" : "off"} ${duration(m.lastSwitch.ago * 1000)} ago${m.lastSwitch.reason ? ` (${m.lastSwitch.reason})` : ""}`));
    }
    if (m.kind === "deph" && !m.settings.guard && m.settings.mode !== "manual") {
      notes.push(h("p", { class: "small warn" }, "No water guard: this pump explodes if its distilled water runs out."));
    }

    const hidden = !!m.settings.hidden;
    const title =
      this.renaming === m.id
        ? h("input", {
            type: "text",
            class: "rename",
            value: m.label ?? "",
            placeholder: machineTitle(m),
            "aria-label": "Name (empty to reset)",
            onkeydown: (e: KeyboardEvent) => {
              if (e.key === "Enter") void this.finishRename(m, (e.currentTarget as HTMLInputElement).value);
              else if (e.key === "Escape") this.cancelRename();
            },
            onblur: (e: Event) => void this.finishRename(m, (e.currentTarget as HTMLInputElement).value),
          })
        : h("strong", { class: "mc-title", title: `${machineTitle(m)}\n${m.id}` }, machineTitle(m));

    return h(
      "article",
      { key: m.id, class: `machine-card ${m.kind}${m.allowed === false ? " off" : ""}${m.stale ? " stale" : ""}${hidden ? " is-hidden" : ""}` },
      h(
        "header",
        null,
        title,
        state,
        h(
          "span",
          { class: "mc-tools" },
          iconButton("edit", "Rename", () => this.startRename(m)),
          hidden
            ? iconButton("show", "Show this machine again", () => void this.saveMachine(m.id, { hidden: false }))
            : iconButton("hide", "Hide this machine (the Butler still switches it)", () => void this.saveMachine(m.id, { hidden: true })),
          m.stale && m.error === "not reported by the Butler" ? iconButton("trash", "Forget this machine", () => void this.forget(m), "danger") : null,
        ),
      ),
      h("div", { class: "muted small mc-sub" }, `${kindLabel(m.kind)}${m.node ? ` · relay "${m.node}"` : ""} · ${m.name ?? "?"}`),
      switchable
        ? h(
            "div",
            { class: "mc-controls" },
            h(
              "select",
              { "aria-label": "Mode", title: MODE_HELP, onchange: (e: Event) => void this.saveMachine(m.id, { mode: (e.currentTarget as HTMLSelectElement).value }) },
              ...POWER_MODES.map((x) => h("option", { value: x, selected: x === m.settings.mode }, MODE_TEXT[x])),
            ),
            this.guardEditor(m),
          )
        : null,
      facts.length ? h("dl", { class: `stats${wide ? "" : " two-col"}` }, ...facts.flatMap(([k, v]) => [h("dt", null, k), h("dd", null, v)])) : null,
      ...notes,
      m.lines.length
        ? h("details", { class: "small" }, h("summary", { class: "muted" }, `Sensor lines (${m.lines.length})`), h("pre", { class: "sensor" }, m.lines.join("\n")))
        : null,
    );
  }

  private guardEditor(m: MachineView): HTMLElement {
    const g = m.settings.guard;
    const current = g ? stackKey(g.stack) : "";
    const known = GUARDS.some((x) => x.key === current);
    // Where the guard reads its stock: AE, or a tank the Butler reads (kept off the ME network).
    const tanks = this.status!.machines.filter((x) => x.kind === "tank");
    // The handlers read the controls of the card on screen: a patch may keep those and drop the ones built here.
    const save = (e: Event) => {
      const box = (e.currentTarget as Element).closest(".guard")!;
      const field = (name: string) => box.querySelector<HTMLInputElement | HTMLSelectElement>(`[name="${name}"]`)?.value ?? "";
      const which = field("guard");
      if (!which) return void this.saveMachine(m.id, { guard: null });
      const stack = GUARDS.find((x) => x.key === which)?.stack ?? g?.stack;
      if (!stack) return;
      const source = field("source");
      void this.saveMachine(m.id, { guard: { stack, min: Number(field("min")), ...(source ? { tank: source } : {}) } });
    };
    const where = g?.tank ? "in the tank" : "in AE";
    const status = m.guard
      ? h(
          "span",
          { class: `small ${m.guard.tripped ? "error" : "muted"}` },
          m.guard.tank && m.guard.amount === undefined
            ? "Holding it off: the tank can't be read"
            : `${m.guard.tripped ? "Holding it off: " : ""}${eu(m.guard.amount)} L ${where}`,
        )
      : null;
    const version = this.status!.butlerVersion;
    const old = g?.tank && version && compareVersions(version, TANK_BUTLER) < 0
      ? h("span", { class: "small warn" }, `The Butler needs v${TANK_BUTLER} to read a tank; until then it reads AE.`)
      : null;
    return h(
      "div",
      { class: "guard", title: `Switched off while ${g?.tank ? "the tank" : "AE"} holds less than this many litres` },
      h(
        "div",
        { class: "guard-row" },
        h(
          "select",
          { name: "guard", "aria-label": "Guard stock", onchange: save },
          h("option", { value: "", selected: !g }, "No guard"),
          ...GUARDS.map((x) => h("option", { value: x.key, selected: x.key === current }, x.label)),
          g && !known ? h("option", { value: current, selected: true }, current) : null,
        ),
        g
          ? h("label", { class: "inline small" }, "<", h("input", { type: "number", name: "min", min: "0", step: "1000", class: "guard-min", value: String(g.min ?? DEFAULT_GUARD.min), "aria-label": "Minimum (L)", onchange: save }), "L")
          : null,
        g && (tanks.length || g.tank)
          ? h(
              "select",
              { name: "source", "aria-label": "Read from", onchange: save },
              h("option", { value: "", selected: !g.tank }, "in AE"),
              ...tanks.map((t) => h("option", { value: t.id, selected: t.id === g.tank }, `in ${machineTitle(t)}`)),
              g.tank && !tanks.some((t) => t.id === g.tank) ? h("option", { value: g.tank, selected: true }, `${g.tank} (not seen)`) : null,
            )
          : null,
      ),
      status,
      old,
    );
  }

  private async saveMachine(id: string, change: Parameters<PowerPage["ctx"]["api"]["updateMachine"]>[1]): Promise<void> {
    const status = await this.attempt(() => this.ctx.api.updateMachine(id, change));
    if (status) this.show(status);
  }

  private startRename(m: MachineView): void {
    this.renaming = m.id;
    this.renderMachines();
    const input = this.grid.querySelector<HTMLInputElement>("input.rename");
    input?.focus();
    input?.select();
  }

  private cancelRename(): void {
    this.renaming = null;
    this.renderMachines();
  }

  private async finishRename(m: MachineView, name: string): Promise<void> {
    if (this.renaming !== m.id) return;
    this.renaming = null;
    const label = name.trim() || null;
    if (label === m.label) return this.renderMachines();
    await this.saveMachine(m.id, { label });
    // Failed: put the old name back.
    if (this.renaming === null) this.renderMachines();
  }

  private async forget(m: MachineView): Promise<void> {
    const status = await this.attempt(() => this.ctx.api.removeMachine(m.id));
    if (status) this.show(status);
  }
}

/** A small button showing only an icon, at a fixed spot on the card. */
function iconButton(icon: "edit" | "hide" | "show" | "trash", label: string, onclick: () => void, extra = ""): HTMLButtonElement {
  return h("button", { type: "button", class: `icon-button${extra ? ` ${extra}` : ""}`, title: label, "aria-label": label, onclick }, h("span", { class: `ico i-${icon}`, "aria-hidden": "true" }));
}
